/**
 * SyncStatus.tsx - Component to display sync status and pending actions
 *
 * Shows:
 * - Count of pending sync items
 * - Ability to manually trigger sync
 * - Sync progress and results
 * - Failed action handling
 */

import React, { useState, useEffect, useCallback } from 'react';
import { backgroundSync, SyncResult, SyncEvent } from '../../services/BackgroundSync';
import { offlineStorage, PendingAction } from '../../services/OfflineStorage';
import './SyncStatus.css';

export interface SyncStatusProps {
  /** Whether to show detailed list of pending actions */
  showDetails?: boolean;
  /** Custom class name */
  className?: string;
  /** Callback when sync completes */
  onSyncComplete?: (result: SyncResult) => void;
}

export const SyncStatus: React.FC<SyncStatusProps> = ({
  showDetails = true,
  className = '',
  onSyncComplete,
}) => {
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([]);
  const [failedActions, setFailedActions] = useState<PendingAction[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState<SyncResult | null>(null);
  const [expanded, setExpanded] = useState(false);

  // Load pending and failed actions
  const loadActions = useCallback(async () => {
    try {
      const [pending, failed] = await Promise.all([
        offlineStorage.getPendingActions('pending'),
        offlineStorage.getPendingActions('failed'),
      ]);
      setPendingActions(pending);
      setFailedActions(failed);
    } catch (error) {
      console.error('Failed to load pending actions:', error);
    }
  }, []);

  // Subscribe to sync events
  useEffect(() => {
    const unsubscribe = backgroundSync.subscribe((event: SyncEvent) => {
      switch (event.type) {
        case 'sync-started':
          setIsSyncing(true);
          setLastSyncResult(null);
          break;
        case 'sync-completed':
          setIsSyncing(false);
          setLastSyncResult(event.data as SyncResult);
          loadActions();
          if (onSyncComplete) {
            onSyncComplete(event.data as SyncResult);
          }
          break;
        case 'sync-failed':
          setIsSyncing(false);
          loadActions();
          break;
        case 'action-synced':
          loadActions();
          break;
      }
    });

    return unsubscribe;
  }, [loadActions, onSyncComplete]);

  // Load actions on mount and periodically
  useEffect(() => {
    loadActions();
    const interval = setInterval(loadActions, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, [loadActions]);

  // Handle manual sync
  const handleSync = async () => {
    if (isSyncing) return;

    try {
      await backgroundSync.syncNow();
    } catch (error) {
      console.error('Manual sync failed:', error);
    }
  };

  // Handle retry failed
  const handleRetryFailed = async () => {
    if (isSyncing) return;

    try {
      await backgroundSync.retryFailed();
    } catch (error) {
      console.error('Retry failed actions failed:', error);
    }
  };

  // Handle clear failed
  const handleClearFailed = async () => {
    try {
      await backgroundSync.clearFailed();
      loadActions();
    } catch (error) {
      console.error('Clear failed actions failed:', error);
    }
  };

  const totalPending = pendingActions.length + failedActions.length;

  // Don't render if nothing to show
  if (totalPending === 0 && !lastSyncResult) {
    return null;
  }

  const formatActionType = (type: string): string => {
    switch (type) {
      case 'purchase_decision':
        return 'Purchase Decision';
      case 'categorization':
        return 'Categorization';
      case 'transaction_update':
        return 'Transaction Update';
      default:
        return type;
    }
  };

  const formatTime = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`sync-status ${className}`}>
      {/* Summary bar */}
      <div
        className="sync-status-summary"
        onClick={() => setExpanded(!expanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setExpanded(!expanded)}
      >
        <div className="sync-status-info">
          {isSyncing ? (
            <>
              <span className="sync-status-icon material-icons spinning">sync</span>
              <span>Syncing...</span>
            </>
          ) : totalPending > 0 ? (
            <>
              <span className="sync-status-icon material-icons">cloud_queue</span>
              <span>
                {pendingActions.length} pending
                {failedActions.length > 0 && `, ${failedActions.length} failed`}
              </span>
            </>
          ) : lastSyncResult ? (
            <>
              <span className="sync-status-icon material-icons success">cloud_done</span>
              <span>
                Synced {lastSyncResult.synced} item{lastSyncResult.synced !== 1 ? 's' : ''}
                {lastSyncResult.conflicts > 0 && ` (${lastSyncResult.conflicts} conflicts)`}
              </span>
            </>
          ) : null}
        </div>

        <div className="sync-status-actions">
          {pendingActions.length > 0 && !isSyncing && navigator.onLine && (
            <button
              className="sync-status-btn sync-now"
              onClick={(e) => {
                e.stopPropagation();
                handleSync();
              }}
              title="Sync now"
            >
              <span className="material-icons">sync</span>
            </button>
          )}
          {showDetails && (
            <span className={`sync-status-expand material-icons ${expanded ? 'expanded' : ''}`}>
              expand_more
            </span>
          )}
        </div>
      </div>

      {/* Detailed view */}
      {showDetails && expanded && (
        <div className="sync-status-details">
          {/* Pending actions */}
          {pendingActions.length > 0 && (
            <div className="sync-status-section">
              <h4>Pending</h4>
              <ul className="sync-action-list">
                {pendingActions.map((action) => (
                  <li key={action.id} className="sync-action-item pending">
                    <span className="sync-action-type">
                      {formatActionType(action.type)}
                    </span>
                    <span className="sync-action-time">
                      {formatTime(action.createdAt)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Failed actions */}
          {failedActions.length > 0 && (
            <div className="sync-status-section failed">
              <h4>Failed</h4>
              <ul className="sync-action-list">
                {failedActions.map((action) => (
                  <li key={action.id} className="sync-action-item failed">
                    <div className="sync-action-info">
                      <span className="sync-action-type">
                        {formatActionType(action.type)}
                      </span>
                      {action.errorMessage && (
                        <span className="sync-action-error">{action.errorMessage}</span>
                      )}
                    </div>
                    <span className="sync-action-retries">
                      {action.retryCount} retries
                    </span>
                  </li>
                ))}
              </ul>
              <div className="sync-status-failed-actions">
                <button
                  className="sync-status-btn retry"
                  onClick={handleRetryFailed}
                  disabled={isSyncing || !navigator.onLine}
                >
                  <span className="material-icons">refresh</span>
                  Retry All
                </button>
                <button
                  className="sync-status-btn clear"
                  onClick={handleClearFailed}
                >
                  <span className="material-icons">delete</span>
                  Clear All
                </button>
              </div>
            </div>
          )}

          {/* Last sync result */}
          {lastSyncResult && (
            <div className="sync-status-section result">
              <h4>Last Sync</h4>
              <div className="sync-result-stats">
                <span className="sync-stat success">
                  <span className="material-icons">check_circle</span>
                  {lastSyncResult.synced} synced
                </span>
                {lastSyncResult.failed > 0 && (
                  <span className="sync-stat failed">
                    <span className="material-icons">error</span>
                    {lastSyncResult.failed} failed
                  </span>
                )}
                {lastSyncResult.conflicts > 0 && (
                  <span className="sync-stat conflict">
                    <span className="material-icons">warning</span>
                    {lastSyncResult.conflicts} conflicts
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SyncStatus;
